import { useState } from "react";
import { Layout } from "@jambushrushti/components/layout/Layout";
import { Seo } from "@jambushrushti/components/shared/Seo";
import { useBooksFromDB } from "@jambushrushti/hooks/useContent";
import { Button } from "@jambushrushti/components/ui/button";
import { Input } from "@jambushrushti/components/ui/input";
import { Card, CardContent } from "@jambushrushti/components/ui/card";
import { Badge } from "@jambushrushti/components/ui/badge";
import {
  Search,
  ExternalLink,
  BookOpen,
  Filter,
  Globe,
  Loader2,
} from "lucide-react";
import { Link } from "react-router-dom";

const bookCategories = [
  "All",
  "Agama",
  "Sutra",
  "Commentary",
  "Grammar",
  "Research",
];

const Books = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const { data: books = [], isLoading } = useBooksFromDB();

  const filteredBooks = books.filter(book => {
    const matchesSearch = book.title
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesCategory =
      selectedCategory === "All" || book.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <Layout>
      <Seo
        title="Books & Publications"
        description="Explore the scholarly works edited and published by Gurudev Muni Jambuvijayji Maharaj Saheb, including critical editions of Jain Agamas and philosophical texts."
      />
      {/* Hero */}
      <section className="bg-gradient-hero lotus-pattern py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="animate-fade-up mx-auto max-w-3xl text-center">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-secondary/80 px-4 py-2 text-sm">
              <BookOpen className="h-4 w-4 text-primary" />
              <span className="text-muted-foreground">
                {books.length} Sacred Texts
              </span>
            </div>
            <h1 className="font-heading mb-6 text-4xl font-bold text-foreground sm:text-5xl">
              Books & Publications
            </h1>
            <p className="font-body text-muted-foreground">
              Explore the scholarly works edited and published by Gurudev Muni
              Jambuvijayji Maharaj Saheb, including critical editions of Jain
              Agamas, Sutras, and philosophical texts.
            </p>
          </div>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="sticky top-16 z-10 border-b border-border bg-background py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search books by title..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              {bookCategories.map(category => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "hero" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Books Grid */}
      <section className="bg-background py-12 lg:py-16">
        <div className="container mx-auto px-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              <div className="mb-6 flex items-center justify-between">
                <p className="text-muted-foreground">
                  Showing {filteredBooks.length} of {books.length} books
                </p>
                {selectedCategory !== "All" && (
                  <Badge variant="secondary" className="gap-1">
                    <Filter className="h-3 w-3" />
                    {selectedCategory}
                  </Badge>
                )}
              </div>

              <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
                {filteredBooks.map((book, index) => (
                  <Link
                    key={book.id}
                    to={`/books/${book.id}`}
                    className="group"
                  >
                    <Card
                      variant="interactive"
                      className="animate-fade-up h-full overflow-hidden"
                      style={{ animationDelay: `${(index % 12) * 50}ms` }}
                    >
                      <div className="relative aspect-square overflow-hidden bg-secondary/50">
                        <img
                          src={
                            book.cover_image ||
                            "https://via.placeholder.com/300x300?text=No+Cover"
                          }
                          alt={book.title}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                        {book.category && (
                          <Badge
                            variant="secondary"
                            className="absolute left-2 top-2 bg-background/90 text-[10px] backdrop-blur-sm"
                          >
                            {book.category}
                          </Badge>
                        )}
                      </div>
                      <CardContent className="p-4">
                        <h3 className="font-heading mb-2 line-clamp-2 font-semibold text-foreground transition-colors group-hover:text-primary">
                          {book.title}
                        </h3>
                        {book.language && (
                          <div className="font-body mb-2 flex items-center gap-1 text-muted-foreground">
                            <Globe className="h-3 w-3" />
                            <span>{book.language}</span>
                          </div>
                        )}
                        <div className="font-body flex items-center gap-1 text-primary">
                          <ExternalLink className="h-3 w-3" />
                          <span>View Details</span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>

              {filteredBooks.length === 0 && (
                <div className="py-16 text-center">
                  <BookOpen className="mx-auto mb-4 h-16 w-16 text-muted-foreground/30" />
                  <h3 className="font-heading mb-2 text-xl font-semibold text-foreground">
                    No books found
                  </h3>
                  <p className="font-body mb-4 text-muted-foreground">
                    Try adjusting your search or filter criteria
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSearchQuery("");
                      setSelectedCategory("All");
                    }}
                  >
                    Clear Filters
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Category Stats */}
      <section className="bg-secondary/30 py-12">
        <div className="container mx-auto px-4">
          <h2 className="font-heading mb-8 text-center text-2xl font-bold text-foreground">
            Browse by Category
          </h2>
          <div className="mx-auto grid max-w-4xl grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
            {bookCategories
              .filter(c => c !== "All")
              .map(category => {
                const count = books.filter(b => b.category === category).length;
                return (
                  <Card
                    key={category}
                    variant="interactive"
                    className="cursor-pointer"
                    onClick={() => setSelectedCategory(category)}
                  >
                    <CardContent className="p-4 text-center">
                      <p className="font-heading font-semibold text-foreground">
                        {category}
                      </p>
                      <p className="font-body text-muted-foreground">
                        {count} books
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
          </div>
        </div>
      </section>

      {/* External Link */}
      <section className="bg-gradient-spiritual py-12">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-heading mb-4 text-2xl font-bold text-foreground">
            Browse Complete Collection
          </h2>
          <p className="font-body mx-auto mb-6 max-w-xl text-muted-foreground">
            Visit the official Siddhi Jambu Parivar website to browse and access
            the complete collection of Gurudev's published works.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button variant="hero" asChild>
              <a
                href="https://siddhijambuparivar.com/shop/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Visit Full Collection
                <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/library">Library Catalog</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Books;
